import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Spinner,
  Badge, Table, Thead, Tbody, Tr, Th, Td,
  Alert, AlertIcon, Select,
} from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');

  const fetchLogs = useCallback(() => {
    setLoading(true);
    const params = actionFilter ? `?action=${actionFilter}` : '';
    fetch(`${API_BASE}/audit-logs/${params}`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => { setLogs(data.results || data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [actionFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const actionColor = (action) => {
    switch (action) {
      case 'pod_delete': return 'red';
      case 'deployment_scale': return 'blue';
      case 'deployment_restart': return 'orange';
      case 'health_check': return 'green';
      case 'api_key_created': return 'purple';
      case 'api_key_deleted': return 'red';
      case 'credential_revealed': return 'yellow';
      case 'credential_reveal_failed': return 'red';
      default: return 'gray';
    }
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="gray.500" /></Box>;

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Audit Log</Heading>
          <Text color="gray.500">{logs.length} log entries</Text>
        </VStack>
        <HStack>
          <Select
            placeholder="All Actions"
            value={actionFilter}
            onChange={e => setActionFilter(e.target.value)}
            size="sm"
            w="200px"
            bg="white"
          >
            <option value="pod_delete">Pod Deleted</option>
            <option value="deployment_scale">Deployment Scaled</option>
            <option value="deployment_restart">Deployment Restarted</option>
            <option value="health_check">Health Check</option>
            <option value="api_key_created">API Key Created</option>
            <option value="api_key_deleted">API Key Deleted</option>
            <option value="credential_revealed">Service Credential Revealed</option>
            <option value="credential_reveal_failed">Service Credential Reveal Failed</option>
          </Select>
          <Button leftIcon={<FiRefreshCw />} colorScheme="gray" onClick={fetchLogs} size="sm">Refresh</Button>
        </HStack>
      </HStack>

      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>Action</Th>
              <Th>Target</Th>
              <Th>Detail</Th>
              <Th>User</Th>
              <Th>IP</Th>
              <Th>Time</Th>
            </Tr>
          </Thead>
          <Tbody>
            {logs.map((log) => (
              <Tr key={log.id} _hover={{ bg: 'gray.50' }}>
                <Td>
                  <Badge colorScheme={actionColor(log.action)} fontSize="xs">{log.action}</Badge>
                </Td>
                <Td><Text fontWeight="medium" fontSize="sm" isTruncated maxW="200px">{log.target}</Text></Td>
                <Td><Text fontSize="xs" color="gray.500" isTruncated maxW="300px">{log.detail}</Text></Td>
                <Td><Text fontSize="sm">{log.username || '—'}</Text></Td>
                <Td><Text fontSize="xs" color="gray.500">{log.ip_address || '—'}</Text></Td>
                <Td>
                  <Text fontSize="xs" color="gray.500">
                    {log.performed_at ? new Date(log.performed_at).toLocaleString() : '—'}
                  </Text>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {logs.length === 0 && (
          <Box textAlign="center" py={10}>
            <Text color="gray.400">No audit log entries found</Text>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default AuditLog;
